import React from 'react';
import { Steps } from 'antd';
import { 
  SearchOutlined,
  ExperimentOutlined,
  CodeOutlined,
  RocketOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { ProjectStage, STAGE_NAMES } from '../../constants/stages';

const StyledSteps = styled(Steps)`
  flex: none;
  overflow: hidden;
  margin: 40px -16px;
  padding: 0 16px;
  height: 200px; // 5个阶段 * 40px 每个阶段的高度
  
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #e8e8e8;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  .ant-steps-item {
    padding: 0;
    height: 40px;
    position: relative;
    cursor: pointer;
    margin: 0;
    
    &::after {
      content: '';
      position: absolute;
      top: 24px;
      right: 8px;
      width: 4px;
      height: 32px;
      background: #d9d9d9;
      opacity: 0.5;
      z-index: 0;
      display: block !important;
    }

    &.ant-steps-item-finish::after {
      background: #1890ff;
    }

    &:last-child::after {
      display: none !important;
    }

    &.selected {
      .ant-steps-item-container {
        background: rgba(24, 144, 255, 0.1);
      }
      
      .ant-steps-item-icon .anticon {
        color: #1890ff;
      }
      
      .ant-steps-item-title {
        color: rgba(0, 0, 0, 0.85);
      }
    }

    &:hover {
      .ant-steps-item-container {
        background: rgba(24, 144, 255, 0.05);
      }
      
      .ant-steps-item-icon .anticon {
        color: #40a9ff;
      }
      
      .ant-steps-item-title {
        color: #1890ff;
      }
    }
  }
  
  .ant-steps-item-container {
    height: 40px;
    padding: 0 8px;
    display: flex;
    align-items: center;
    transition: all 0.3s ease;
  }
  
  .ant-steps-item-tail {
    display: none !important;
  }

  .ant-steps-item-icon {
    margin: 0 8px 0 0;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.3s ease;
    
    .anticon {
      font-size: 22px;
      color: rgba(0, 0, 0, 0.45);
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      transition: all 0.3s ease;
    }
  }

  .ant-steps-item-content {
    height: 40px;
    display: flex;
    align-items: center;
  }
  
  .ant-steps-item-title {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.45);
    margin: 0;
    padding: 0;
    height: 100%;
    display: flex;
    align-items: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: all 0.3s ease;
  }

  .ant-steps-item-wait .ant-steps-item-icon .anticon {
    color: rgba(0, 0, 0, 0.25);
  }
`;

const StatusIndicator = styled.div`
  position: absolute;
  right: 0;
  top: 10px;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 1;
  z-index: 1;
  font-family: "Segoe UI", sans-serif;
  border-radius: 50%;
  border: 1.5px solid;
  background: white;
  font-weight: bold;

  &::before {
    content: attr(data-icon);
    position: relative;
    top: 0;
  }

  &[data-icon="●"] {
    font-size: 11px;
    
    &::before {
      content: "⬤";
    }
  }

  &[data-icon="○"] {
    font-size: 11px;
    
    &::before {
      content: "⭘";
    }
  }

  &[data-icon="✓"] {
    font-size: 13px;
    
    &::before {
      content: "✔";
    }
  }
`;

interface StageItem {
  title: string;
  icon: React.ReactNode;
  stage: ProjectStage;
}

const STAGE_ICONS: Record<ProjectStage, React.ReactNode> = {
  [ProjectStage.PROBLEM_DEFINITION]: <SearchOutlined />,
  [ProjectStage.SOLUTION_TESTING]: <ExperimentOutlined />,
  [ProjectStage.MVP_CREATION]: <CodeOutlined />,
  [ProjectStage.LAUNCH_EXPERIMENT]: <RocketOutlined />,
  [ProjectStage.SCALABLE_GROWTH]: <LineChartOutlined />
};

const STAGE_ITEMS: StageItem[] = Object.keys(ProjectStage)
  .filter(key => !isNaN(Number(key)))
  .map(key => {
    const stage = Number(key) as ProjectStage;
    return {
      title: STAGE_NAMES[stage],
      icon: STAGE_ICONS[stage],
      stage
    };
  });

interface StageProgressProps {
  currentStage: ProjectStage;
  selectedStage: ProjectStage;
  onStageSelect: (stage: ProjectStage) => void;
}

const StageProgress: React.FC<StageProgressProps> = ({
  currentStage,
  selectedStage,
  onStageSelect
}) => {
  const handleStageClick = (index: number) => {
    onStageSelect(STAGE_ITEMS[index].stage);
  };

  const items = STAGE_ITEMS.map(item => ({
    title: item.title,
    icon: item.icon,
    status: item.stage < currentStage ? 'finish' as const :
           item.stage === currentStage ? 'process' as const :
           'wait' as const,
    className: selectedStage === item.stage ? 'selected' : '',
    onClick: () => onStageSelect(item.stage),
    description: (
      <StatusIndicator 
        style={{ 
          color: item.stage < currentStage ? '#1890ff' : 
                item.stage === currentStage ? '#1890ff' : 
                '#d9d9d9',
          borderColor: item.stage < currentStage ? '#1890ff' : 
                      item.stage === currentStage ? '#1890ff' : 
                      '#d9d9d9'
        }}
        data-icon={
          item.stage < currentStage ? '✓' : 
          item.stage === currentStage ? '●' : 
          '○'
        }
      />
    )
  }));

  return (
    <StyledSteps
      direction="vertical"
      size="small"
      current={currentStage}
      items={items}
      onChange={handleStageClick}
    />
  );
};

export default StageProgress; 